import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import api from "../lib/api";
import { useData } from "../lib/DataContext";
import { useAuth } from "../lib/AuthContext";
import TaskTable from "../components/TaskTable";
import TaskDrawer from "../components/TaskDrawer";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { ArrowLeft, CheckSquare, FileText, ClipboardList, FolderArchive, MessagesSquare, Download, Trash2, EyeOff, Eye, Plus, Upload, Pencil, Users, X, Activity, Share2, MessageSquare, Copy, Clock, ScanEye, Star, ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Table as TableIcon } from "lucide-react";

const TILES = [
  { key: "tasks", label: "Tasks", icon: CheckSquare, color: "bg-blue-100 text-blue-600" },
  { key: "notes", label: "Notes", icon: FileText, color: "bg-emerald-100 text-emerald-600" },
  { key: "tables", label: "Tables", icon: TableIcon, color: "bg-cyan-100 text-cyan-600" },
  { key: "mom", label: "MOM", icon: ClipboardList, color: "bg-amber-100 text-amber-600" },
  { key: "documents", label: "Document Vault", icon: FolderArchive, color: "bg-violet-100 text-violet-600" },
  { key: "collaborations", label: "Collaborations", icon: MessagesSquare, color: "bg-rose-100 text-rose-600" },
  { key: "activity", label: "Activity Feed", icon: Activity, color: "bg-slate-100 text-slate-600" },
];

export default function WorkspaceHome() {
  const { wsId } = useParams();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const threadId = params.get("thread");
  const [ws, setWs] = useState(null);
  const [active, setActive] = useState(threadId ? "collaborations" : null);
  const [showMembers, setShowMembers] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await api.get(`/workspaces/${wsId}`); setWs(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cannot open workspace"); nav("/workspaces"); }
  }, [wsId, nav]);
  useEffect(() => { load(); }, [load]);

  const openTile = async (key) => {
    setActive(key);
    if (key === "collaborations") {
      try { await api.post(`/workspaces/${wsId}/collab-seen`); setWs((w) => w ? { ...w, collab_unread: 0 } : w); } catch {}
    }
  };

  const exportWs = async (fmt) => {
    const res = await api.get(`/workspaces/${wsId}/export`, { params: { fmt }, responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = `${ws?.name || "workspace"}.${fmt === "excel" ? "xlsx" : fmt}`; a.click();
  };

  if (!ws) return null;
  return (
    <div className="p-6" data-testid="workspace-home">
      <button onClick={() => nav("/workspaces")} className="flex items-center gap-1 text-sm text-slate-500 hover:text-slate-800 mb-3" data-testid="ws-back"><ArrowLeft size={15} /> Workspaces</button>

      {/* Header */}
      <div className="bg-white border rounded-xl p-5 mb-5" data-testid="ws-header">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900">{ws.name}</h1>
            <div className="text-sm text-slate-500 mt-1">{ws.human_id} · {ws.status} · Owner: {ws.owner_name} · {(ws.members?.length || 0) + 1} member(s) · Your role: {ws.my_role}</div>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {ws.project_id && (
              <Button variant="outline" size="sm" data-testid="ws-goto-project" onClick={() => nav("/projects")}>Go to Project</Button>
            )}
            {["owner", "admin"].includes(ws.my_role) && (
              <Button variant="outline" size="sm" data-testid="ws-members-btn" onClick={() => setShowMembers(true)}><Users size={14} className="mr-1" /> Members ({(ws.members?.length || 0) + 1})</Button>
            )}
            <div className="relative group">
              <Button variant="outline" size="sm" data-testid="ws-export-btn"><Download size={14} className="mr-1" /> Export</Button>
              <div className="absolute right-0 mt-1 w-32 bg-white border rounded-lg shadow-lg z-20 hidden group-hover:block" data-testid="ws-export-menu">
                {["json", "excel", "pdf", "zip"].map((f) => (
                  <button key={f} onClick={() => exportWs(f)} data-testid={`ws-export-${f}`} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50 uppercase">{f}</button>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>

      {!active ? (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-5 gap-4" data-testid="ws-tiles">
          {TILES.map((t) => (
            <button key={t.key} data-testid={`ws-tile-${t.key}`} onClick={() => openTile(t.key)}
              className="relative bg-white border rounded-xl p-6 flex flex-col items-center justify-center gap-3 hover:shadow-lg hover:-translate-y-0.5 transition-all min-h-[140px]">
              {t.key === "collaborations" && ws.collab_unread > 0 && <span data-testid="collab-unread-dot" className="absolute top-3 right-3 h-2.5 w-2.5 bg-rose-500 rounded-full" />}
              <div className={`h-12 w-12 rounded-xl flex items-center justify-center ${t.color}`}><t.icon size={24} /></div>
              <div className="font-semibold text-slate-800">{t.label}</div>
              {t.key !== "activity" && <div className="text-xs text-slate-400">{ws.counts[t.key]} {t.key === "tasks" ? "open" : "items"}</div>}
            </button>
          ))}
        </div>
      ) : (
        <div>
          <div className="flex items-center gap-2 mb-4 flex-wrap">
            {TILES.map((t) => (
              <button key={t.key} data-testid={`ws-tab-${t.key}`} onClick={() => openTile(t.key)}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-sm ${active === t.key ? "bg-slate-800 text-white" : "bg-white border text-slate-600 hover:bg-slate-50"}`}>
                <t.icon size={14} /> {t.label}
              </button>
            ))}
          </div>
          {active === "tasks" && <TasksPanel wsId={wsId} ws={ws} onChange={load} />}
          {active === "documents" && <DocumentsPanel wsId={wsId} onChange={load} />}
          {["notes", "mom"].includes(active) && <ItemsPanel key={active} wsId={wsId} ws={ws} module={active} onChange={load} />}
          {active === "tables" && <TablesPanel wsId={wsId} onChange={load} />}
          {active === "collaborations" && <CollabPanel wsId={wsId} onChange={load} highlightId={threadId} />}
          {active === "activity" && <ActivityPanel wsId={wsId} />}
        </div>
      )}
      {showMembers && <MembersModal ws={ws} onClose={() => setShowMembers(false)} onDone={load} />}
    </div>
  );
}

function MembersModal({ ws, onClose, onDone }) {
  const { users } = useData();
  const [members, setMembers] = useState(ws.members || []);
  const [uid, setUid] = useState("");
  const [role, setRole] = useState("viewer");
  const WS_ROLES = ["admin", "editor", "contributor", "viewer"];
  const uname = (id) => users.find((u) => u.id === id)?.full_name || id;
  const reload = async () => { const { data } = await api.get(`/workspaces/${ws.id}/members`); setMembers(data); onDone?.(); };
  const add = async () => {
    if (!uid) return toast.error("Select a user");
    try { await api.post(`/workspaces/${ws.id}/members`, { user_id: uid, ws_role: role }); toast.success("Member added"); setUid(""); reload(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const remove = async (id) => { try { await api.delete(`/workspaces/${ws.id}/members/${id}`); toast.success("Member removed"); reload(); } catch (e) { toast.error("Failed"); } };
  const available = users.filter((u) => u.id !== ws.owner_id && u.active !== false && !members.some((m) => m.user_id === u.id));
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="ws-members-modal">
      <div className="bg-white rounded-xl w-full max-w-lg p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-slate-900">Manage Members</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="space-y-1.5 mb-4 max-h-64 overflow-y-auto">
          <div className="flex items-center justify-between text-sm border rounded-md px-3 py-2 bg-indigo-50">
            <span className="font-medium">{ws.owner_name} <span className="text-xs text-indigo-600">· Owner</span></span>
          </div>
          {members.map((m) => (
            <div key={m.id} data-testid={`ws-member-${m.user_id}`} className="flex items-center justify-between text-sm border rounded-md px-3 py-2 gap-2">
              <span className="flex-1 min-w-0 truncate">{uname(m.user_id)}</span>
              <select data-testid={`ws-member-role-${m.user_id}`} value={m.ws_role}
                onChange={async (e) => { try { await api.post(`/workspaces/${ws.id}/members`, { user_id: m.user_id, ws_role: e.target.value }); toast.success("Role updated"); reload(); } catch { toast.error("Failed"); } }}
                className="h-8 rounded-md border border-input bg-background px-2 text-xs capitalize">
                {WS_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
              <button data-testid={`ws-member-remove-${m.user_id}`} onClick={() => remove(m.user_id)} className="text-slate-400 hover:text-red-500"><Trash2 size={15} /></button>
            </div>
          ))}
          {members.length === 0 && <div className="text-sm text-slate-400">No additional members yet.</div>}
        </div>
        <div className="border-t pt-4 flex items-end gap-2">
          <div className="flex-1">
            <label className="text-xs text-slate-500">User</label>
            <select data-testid="ws-member-user" value={uid} onChange={(e) => setUid(e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm">
              <option value="">Select user…</option>
              {available.map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select>
          </div>
          <div className="w-36">
            <label className="text-xs text-slate-500">Role</label>
            <select data-testid="ws-member-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm capitalize">
              {WS_ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <Button onClick={add} data-testid="ws-member-add"><Plus size={15} className="mr-1" /> Add</Button>
        </div>
      </div>
    </div>
  );
}

function TasksPanel({ wsId, ws, onChange }) {
  const { user } = useAuth();
  const [tasks, setTasks] = useState([]);
  const [openId, setOpenId] = useState(null);
  const [title, setTitle] = useState("");
  const load = useCallback(() => api.get("/tasks", { params: { workspace_id: wsId, page_size: 200 } }).then((r) => setTasks(r.data.items)), [wsId]);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!title.trim()) return;
    await api.post("/tasks", { title: title.trim(), primary_owner_id: user.id, current_action_owner_id: user.id, workspace_id: wsId, department_id: ws.department_id, project_id: ws.project_id });
    toast.success("Task created in workspace"); setTitle(""); load(); onChange?.();
  };
  return (
    <div>
      <div className="flex gap-2 mb-3">
        <Input data-testid="ws-task-title" placeholder="New task title…" value={title} onChange={(e) => setTitle(e.target.value)} onKeyDown={(e) => e.key === "Enter" && create()} className="max-w-sm" />
        <Button onClick={create} data-testid="ws-task-create"><Plus size={16} className="mr-1" /> Add Task</Button>
      </div>
      <TaskTable tasks={tasks} onOpen={setOpenId} />
      {openId && <TaskDrawer taskId={openId} onClose={() => setOpenId(null)} onChanged={() => { load(); onChange?.(); }} />}
    </div>
  );
}

function DataTable({ columns, rows, colTypes }) {
  const { users } = useData();
  const types = colTypes || columns.map(() => "text");
  const uname = (id) => users.find((u) => u.id === id)?.full_name || id;
  return (
    <div className="overflow-x-auto mt-2" data-testid="table-grid">
      <table className="w-full border-collapse text-sm">
        <thead><tr>{columns.map((c, i) => <th key={i} className="border border-slate-300 bg-slate-50 px-2 py-1.5 text-left font-semibold text-slate-700 min-w-28">{c}</th>)}</tr></thead>
        <tbody>
          {rows.length === 0 && <tr><td colSpan={columns.length} className="border border-slate-300 px-2 py-2 text-slate-400 text-xs">No rows.</td></tr>}
          {rows.map((r, i) => <tr key={i}>{columns.map((_, j) => <td key={j} className="border border-slate-300 px-2 py-1.5 align-top text-slate-700 whitespace-pre-wrap">{types[j] === "user" && r[j] ? uname(r[j]) : r[j]}</td>)}</tr>)}
        </tbody>
      </table>
    </div>
  );
}

function ShareTableModal({ wsId, table, onClose }) {
  const { users } = useData();
  const [uid, setUid] = useState("");
  const [role, setRole] = useState("viewer");
  const shareIt = async () => {
    if (!uid) return toast.error("Select a user");
    try { await api.post(`/workspaces/${wsId}/members`, { user_id: uid, ws_role: role }); toast.success("Shared — user added to this workspace"); onClose(); }
    catch (e) { toast.error(e.response?.data?.detail || "Share failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={onClose} data-testid="table-share-modal">
      <div className="bg-white rounded-xl w-full max-w-md p-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-3"><h3 className="text-lg font-semibold text-slate-900">Share &ldquo;{table.title}&rdquo;</h3><button onClick={onClose} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
        <p className="text-xs text-slate-500 mb-3">Tables are shared via workspace membership — the user gets access to this workspace with the chosen role.</p>
        <div className="flex items-end gap-2">
          <div className="flex-1"><label className="text-xs text-slate-500">User</label>
            <select data-testid="table-share-user" value={uid} onChange={(e) => setUid(e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm">
              <option value="">Select user…</option>
              {users.filter((u) => u.active !== false).map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
            </select></div>
          <div className="w-32"><label className="text-xs text-slate-500">Role</label>
            <select data-testid="table-share-role" value={role} onChange={(e) => setRole(e.target.value)} className="w-full h-9 rounded-md border border-input bg-background px-2 text-sm capitalize">
              {["viewer", "contributor", "editor", "admin"].map((r) => <option key={r} value={r}>{r}</option>)}
            </select></div>
          <Button onClick={shareIt} data-testid="table-share-submit">Share</Button>
        </div>
      </div>
    </div>
  );
}

function CellInput({ type, value, onChange, testid }) {
  const { users } = useData();
  if (type === "date") return <Input type="date" data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="h-8 border-0 shadow-none focus-visible:ring-0 px-1.5 text-xs" />;
  if (type === "user") return (
    <select data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="w-full h-8 rounded border-0 bg-transparent px-1.5 text-sm focus:outline-none">
      <option value="">Select user…</option>
      {users.filter((u) => u.active !== false).map((u) => <option key={u.id} value={u.id}>{u.full_name}</option>)}
    </select>
  );
  return <Textarea rows={1} data-testid={testid} value={value} onChange={(e) => onChange(e.target.value)} className="border-0 shadow-none focus-visible:ring-0 px-1.5 min-h-8" />;
}

function TablesPanel({ wsId, onChange }) {
  const [tables, setTables] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [builder, setBuilder] = useState(null);
  const [rename, setRename] = useState(null);
  const [share, setShare] = useState(null);
  const [exporting, setExporting] = useState(null);
  const load = useCallback(() => api.get(`/workspaces/${wsId}/items/tables`, { params: { show_hidden: showHidden } }).then((r) => setTables(r.data)), [wsId, showHidden]);
  useEffect(() => { load(); }, [load]);

  const newBuilder = () => setBuilder({ id: null, name: "", description: "", columns: ["Sr. No.", "Column 1", "Column 2"], col_types: ["text", "text", "text"], rows: [["", "", ""]] });
  const editBuilder = (t) => setBuilder({ id: t.id, name: t.title || "", description: t.description || "", columns: [...(t.columns || [])], col_types: t.col_types || (t.columns || []).map(() => "text"), rows: (t.rows || []).map((r) => [...r]) });
  const setCell = (ri, ci, v) => setBuilder((b) => ({ ...b, rows: b.rows.map((r, i) => (i === ri ? r.map((c, j) => (j === ci ? v : c)) : r)) }));
  const setCol = (ci, v) => setBuilder((b) => ({ ...b, columns: b.columns.map((c, j) => (j === ci ? v : c)) }));
  const setColType = (ci, v) => setBuilder((b) => ({ ...b, col_types: b.col_types.map((c, j) => (j === ci ? v : c)) }));
  const addRow = () => setBuilder((b) => ({ ...b, rows: [...b.rows, b.columns.map(() => "")] }));
  const delRow = (ri) => setBuilder((b) => (b.rows.length > 1 ? { ...b, rows: b.rows.filter((_, i) => i !== ri) } : b));
  const moveRow = (ri, d) => setBuilder((b) => { const j = ri + d; if (j < 0 || j >= b.rows.length) return b; const rows = [...b.rows]; [rows[ri], rows[j]] = [rows[j], rows[ri]]; return { ...b, rows }; });
  const addCol = () => setBuilder((b) => ({ ...b, columns: [...b.columns, `Column ${b.columns.length + 1}`], col_types: [...b.col_types, "text"], rows: b.rows.map((r) => [...r, ""]) }));
  const delCol = (ci) => setBuilder((b) => (b.columns.length > 1 ? { ...b, columns: b.columns.filter((_, j) => j !== ci), col_types: b.col_types.filter((_, j) => j !== ci), rows: b.rows.map((r) => r.filter((_, j) => j !== ci)) } : b));
  const moveCol = (ci, d) => setBuilder((b) => { const j = ci + d; if (j < 0 || j >= b.columns.length) return b; const swap = (arr) => { const a = [...arr]; [a[ci], a[j]] = [a[j], a[ci]]; return a; }; return { ...b, columns: swap(b.columns), col_types: swap(b.col_types), rows: b.rows.map(swap) }; });

  const save = async () => {
    if (!builder.name.trim()) return toast.error("Table name required");
    if (!builder.columns.some((c) => c.trim())) return toast.error("Add at least one column");
    const payload = { title: builder.name.trim(), description: builder.description.trim(), columns: builder.columns.map((c) => c.trim() || "Column"), col_types: builder.col_types, rows: builder.rows, content: builder.name.trim() };
    try {
      if (builder.id) { await api.put(`/workspaces/${wsId}/items/tables/${builder.id}`, payload); toast.success("Table saved"); }
      else { await api.post(`/workspaces/${wsId}/items/tables`, payload); toast.success("Table created"); }
      setBuilder(null); load(); onChange?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
  };
  const op = async (t, o) => {
    if (o === "delete" && !window.confirm(`Delete table "${t.title}"? It can be restored later.`)) return;
    await api.post(`/workspaces/${wsId}/items/tables/${t.id}/op/${o}`);
    load(); onChange?.();
  };
  const doRename = async () => {
    if (!rename?.name?.trim()) return toast.error("Name required");
    try { await api.put(`/workspaces/${wsId}/items/tables/${rename.id}`, { title: rename.name.trim() }); toast.success("Renamed"); setRename(null); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Rename failed"); }
  };
  const printTable = (t) => {
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const w = window.open("", "_blank");
    if (!w) return toast.error("Popup blocked — allow popups to print");
    w.document.write(`<html><head><title>${esc(t.title)}</title><style>body{font-family:Arial,sans-serif;margin:24px}h2{margin:0 0 4px}p{margin:0 0 14px;color:#444}table{border-collapse:collapse;width:100%}th,td{border:1px solid #64748b;padding:6px 8px;vertical-align:top;white-space:pre-wrap;font-size:12px;text-align:left}th{background:#f1f5f9}tr{page-break-inside:avoid}@page{margin:12mm}</style></head><body><h2>${esc(t.title)}</h2>${t.description ? `<p>${esc(t.description)}</p>` : ""}<table><thead><tr>${(t.columns || []).map((c) => `<th>${esc(c)}</th>`).join("")}</tr></thead><tbody>${(t.rows || []).map((r) => `<tr>${(t.columns || []).map((_, j) => `<td>${esc(r[j])}</td>`).join("")}</tr>`).join("")}</tbody></table></body></html>`);
    w.document.close(); w.focus(); w.print();
  };
  const exportTable = async (t, fmt) => {
    if (fmt === "print") return printTable(t);
    if (exporting) return;
    setExporting(t.id);
    try {
      const res = await api.get(`/workspaces/${wsId}/tables/${t.id}/export`, { params: { fmt }, responseType: "blob" });
      const match = (res.headers?.["content-disposition"] || "").match(/filename="([^"]+)"/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = match?.[1] || `${t.title}.${fmt === "excel" ? "xlsx" : fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt === "excel" ? "Excel" : fmt.toUpperCase()} downloaded`);
    } catch { toast.error("Export failed"); }
    finally { setExporting(null); }
  };

  const iconBtn = "p-1.5 rounded text-slate-400 hover:bg-slate-100";
  return (
    <div data-testid="tables-panel">
      {!builder ? (
        <div className="flex items-center gap-2 mb-4">
          <Button size="sm" onClick={newBuilder} data-testid="table-create"><Plus size={15} className="mr-1" /> Create Table</Button>
          <button onClick={() => setShowHidden((s) => !s)} data-testid="table-show-hidden" className={`flex items-center gap-1 text-sm px-2.5 py-1.5 rounded-md border ${showHidden ? "bg-slate-800 text-white" : "text-slate-600"}`}><Eye size={14} /> {showHidden ? "Showing Hidden" : "Show Hidden"}</button>
        </div>
      ) : (
        <div className="bg-white border rounded-lg p-4 mb-4 space-y-2" data-testid="table-builder">
          <div className="flex gap-2 flex-wrap">
            <Input data-testid="table-name" placeholder="Table name *" value={builder.name} onChange={(e) => setBuilder({ ...builder, name: e.target.value })} className="max-w-xs" />
            <Input data-testid="table-desc" placeholder="Description (optional)" value={builder.description} onChange={(e) => setBuilder({ ...builder, description: e.target.value })} className="flex-1 min-w-40" />
          </div>
          <div className="overflow-x-auto">
            <table className="w-full border-collapse text-sm">
              <thead>
                <tr>
                  {builder.columns.map((c, ci) => (
                    <th key={ci} className="border border-slate-300 bg-slate-50 p-1 min-w-32">
                      <div className="flex items-center gap-0.5">
                        <button data-testid={`table-col-left-${ci}`} onClick={() => moveCol(ci, -1)} disabled={ci === 0} className="p-0.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronLeft size={13} /></button>
                        <Input data-testid={`table-col-name-${ci}`} value={c} onChange={(e) => setCol(ci, e.target.value)} className="h-7 border-0 shadow-none focus-visible:ring-0 px-1 text-xs font-semibold bg-transparent" />
                        <button data-testid={`table-col-right-${ci}`} onClick={() => moveCol(ci, 1)} disabled={ci === builder.columns.length - 1} className="p-0.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronRight size={13} /></button>
                        <button data-testid={`table-col-del-${ci}`} onClick={() => delCol(ci)} disabled={builder.columns.length === 1} className="p-0.5 rounded text-slate-400 hover:text-red-600 disabled:opacity-30"><Trash2 size={13} /></button>
                      </div>
                      <select data-testid={`table-col-type-${ci}`} value={builder.col_types[ci] || "text"} onChange={(e) => setColType(ci, e.target.value)}
                        className="mt-0.5 w-full h-6 rounded border border-slate-200 bg-white px-1 text-[10px] text-slate-500">
                        <option value="text">text</option><option value="date">date</option><option value="user">user</option>
                      </select>
                    </th>
                  ))}
                  <th className="border border-slate-300 bg-slate-50 w-16"></th>
                </tr>
              </thead>
              <tbody>
                {builder.rows.map((r, ri) => (
                  <tr key={ri}>
                    {builder.columns.map((_, ci) => (
                      <td key={ci} className="border border-slate-300 p-1 align-top"><CellInput type={builder.col_types[ci] || "text"} testid={`table-cell-${ri}-${ci}`} value={r[ci] ?? ""} onChange={(v) => setCell(ri, ci, v)} /></td>
                    ))}
                    <td className="border border-slate-300 p-1 align-top text-center whitespace-nowrap">
                      <button data-testid={`table-row-up-${ri}`} onClick={() => moveRow(ri, -1)} disabled={ri === 0} className="p-0.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronUp size={13} /></button>
                      <button data-testid={`table-row-down-${ri}`} onClick={() => moveRow(ri, 1)} disabled={ri === builder.rows.length - 1} className="p-0.5 rounded text-slate-400 hover:text-slate-700 disabled:opacity-30"><ChevronDown size={13} /></button>
                      <button data-testid={`table-row-del-${ri}`} onClick={() => delRow(ri)} disabled={builder.rows.length === 1} className="p-0.5 rounded text-slate-400 hover:text-red-600 disabled:opacity-30"><Trash2 size={13} /></button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <Button size="sm" variant="outline" onClick={addRow} data-testid="table-add-row"><Plus size={14} className="mr-1" /> Add Row</Button>
            <Button size="sm" variant="outline" onClick={addCol} data-testid="table-add-col"><Plus size={14} className="mr-1" /> Add Column</Button>
            <Button size="sm" onClick={save} data-testid="table-save">{builder.id ? "Save Table" : "Save Table"}</Button>
            <Button size="sm" variant="ghost" onClick={() => setBuilder(null)} data-testid="table-cancel">Cancel</Button>
          </div>
        </div>
      )}

      <div className="space-y-3">
        {tables.length === 0 && !builder && <div className="text-sm text-slate-400">No tables yet.</div>}
        {tables.map((t) => (
          <div key={t.id} data-testid={`table-${t.id}`} className="bg-white border rounded-lg p-4">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-medium text-slate-800 flex items-center gap-1.5">{t.is_hidden && <span className="text-[10px] bg-slate-100 rounded px-1"><EyeOff size={10} className="inline" /> Hidden</span>}{t.title}</div>
                {t.description && <div className="text-xs text-slate-500 mt-0.5">{t.description}</div>}
                <div className="text-[11px] text-slate-400 mt-1">{t.created_by_name} · Created {new Date(t.created_at).toLocaleString()} · Updated {new Date(t.updated_at).toLocaleString()} · {t.is_hidden ? "Hidden" : "Active"}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button data-testid={`table-edit-${t.id}`} onClick={() => editBuilder(t)} title="Edit table" className={`${iconBtn} hover:text-blue-600 hover:bg-blue-50`}><Pencil size={14} /></button>
                <button data-testid={`table-rename-${t.id}`} onClick={() => setRename({ id: t.id, name: t.title })} title="Rename" className={`${iconBtn} hover:text-blue-600 hover:bg-blue-50`}><FileText size={14} /></button>
                <button data-testid={`table-share-${t.id}`} onClick={() => setShare(t)} title="Share" className={`${iconBtn} hover:text-blue-600 hover:bg-blue-50`}><Share2 size={14} /></button>
                <div className="relative group">
                  <button data-testid={`table-export-${t.id}`} disabled={exporting === t.id} title="Export" className={`${iconBtn} hover:text-emerald-600 hover:bg-emerald-50 disabled:opacity-40`}><Download size={14} /></button>
                  <div className="absolute right-0 mt-1 w-32 bg-white border rounded-lg shadow-lg z-20 hidden group-hover:block" data-testid={`table-export-menu-${t.id}`}>
                    {[["excel", "Excel (.xlsx)"], ["pdf", "PDF"], ["print", "Print"], ["csv", "CSV"]].map(([f, lbl]) => (
                      <button key={f} onClick={() => exportTable(t, f)} data-testid={`table-export-${t.id}-${f}`} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50">{lbl}</button>
                    ))}
                  </div>
                </div>
                <button data-testid={`table-hide-${t.id}`} onClick={() => op(t, t.is_hidden ? "unhide" : "hide")} title={t.is_hidden ? "Unhide" : "Hide"} className={iconBtn}>{t.is_hidden ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                <button data-testid={`table-delete-${t.id}`} onClick={() => op(t, "delete")} title="Delete" className={`${iconBtn} hover:text-red-600 hover:bg-red-50`}><Trash2 size={14} /></button>
              </div>
            </div>
            <DataTable columns={t.columns || []} rows={t.rows || []} colTypes={t.col_types} />
          </div>
        ))}
      </div>

      {rename && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setRename(null)} data-testid="table-rename-modal">
          <div className="bg-white rounded-xl w-full max-w-sm p-5" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-3"><h3 className="text-lg font-semibold text-slate-900">Rename Table</h3><button onClick={() => setRename(null)} className="text-slate-400 hover:text-slate-700"><X size={18} /></button></div>
            <Input data-testid="table-rename-input" value={rename.name} onChange={(e) => setRename({ ...rename, name: e.target.value })} />
            <div className="flex justify-end gap-2 mt-4"><Button variant="outline" size="sm" onClick={() => setRename(null)}>Cancel</Button><Button size="sm" onClick={doRename} data-testid="table-rename-save">Save</Button></div>
          </div>
        </div>
      )}
      {share && <ShareTableModal wsId={wsId} table={share} onClose={() => setShare(null)} />}
    </div>
  );
}

const EMPTY_MOM_ROW = { subject: "", discussions: "", action: "", ownership: "", timeline: "", remarks: "" };
const MOM_COLS = ["Sr. No.", "Subject", "Discussions", "Action / Next Steps", "Ownership", "Timeline", "Remarks"];
// Reads new keys with fallback to the old combined keys so historical MOMs render unchanged.
const MOM_ROW = (r) => ({
  subject: r.subject || r.agenda || "",
  discussions: r.discussions || r.discussion || "",
  action: r.action || "",
  ownership: r.ownership || "",
  timeline: r.timeline || "",
  remarks: r.remarks || r.remark || "",
});

function MomTable({ rows, onRowTask }) {
  return (
    <div className="overflow-x-auto mt-1.5" data-testid="mom-table">
      <table className="w-full border-collapse text-sm">
        <thead>
          <tr>{MOM_COLS.map((c) => <th key={c} className={`border border-slate-300 bg-slate-50 px-2 py-1.5 text-left font-semibold text-slate-700 ${c === "Sr. No." ? "w-12" : ""}`}>{c}</th>)}</tr>
        </thead>
        <tbody>
          {rows.map((r, i) => {
            const v = MOM_ROW(r);
            return (
            <tr key={i}>
              <td className="border border-slate-300 px-2 py-1.5 align-top text-slate-500">{i + 1}</td>
              <td className="border border-slate-300 px-2 py-1.5 align-top font-medium text-slate-800 whitespace-pre-wrap min-w-36">{v.subject}</td>
              <td className="border border-slate-300 px-2 py-1.5 align-top text-slate-700 whitespace-pre-wrap min-w-52">{v.discussions}</td>
              <td className="border border-slate-300 px-2 py-1.5 align-top text-slate-700 whitespace-pre-wrap min-w-52">
                {v.action}
                {onRowTask && v.action && (
                  <button data-testid={`mom-row-task-${i}`} onClick={() => onRowTask(r, i)} title="Create task from this action"
                    className="mt-1 flex items-center gap-1 text-[11px] font-medium text-blue-600 hover:text-blue-800 border border-blue-200 hover:border-blue-400 rounded px-1.5 py-0.5 whitespace-normal">
                    <Plus size={11} /> Create task
                  </button>
                )}
              </td>
              <td className="border border-slate-300 px-2 py-1.5 align-top text-slate-700 whitespace-pre-wrap min-w-28">{v.ownership}</td>
              <td className="border border-slate-300 px-2 py-1.5 align-top text-slate-700 whitespace-pre-wrap min-w-24">{v.timeline}</td>
              <td className="border border-slate-300 px-2 py-1.5 align-top text-slate-700 whitespace-pre-wrap min-w-24">{v.remarks}</td>
            </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function ItemsPanel({ wsId, ws, module, onChange }) {
  const [items, setItems] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [form, setForm] = useState({ title: "", content: "" });
  const [momDate, setMomDate] = useState("");
  const [momRows, setMomRows] = useState([{ ...EMPTY_MOM_ROW }]);
  const [editId, setEditId] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [shareMom, setShareMom] = useState(null);
  const load = useCallback(() => api.get(`/workspaces/${wsId}/items/${module}`, { params: { show_hidden: showHidden } }).then((r) => setItems(r.data)), [wsId, module, showHidden]);
  useEffect(() => { load(); }, [load]);
  const setMomRow = (i, k, v) => setMomRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const resetForm = () => { setForm({ title: "", content: "" }); setMomDate(""); setMomRows([{ ...EMPTY_MOM_ROW }]); setEditId(null); };
  const startEdit = (it) => {
    setForm({ title: it.title || "", content: it.content || "" });
    setMomDate(it.date || "");
    setMomRows((it.rows || []).map((r) => MOM_ROW(r)));
    setEditId(it.id);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };
  const momPayload = () => {
    const rows = momRows.map((r) => ({ subject: r.subject.trim(), discussions: r.discussions.trim(), action: r.action.trim(), ownership: r.ownership.trim(), timeline: r.timeline.trim(), remarks: r.remarks.trim() }))
      .filter((r) => r.subject || r.discussions || r.action || r.ownership || r.timeline || r.remarks);
    if (!rows.length) return null;
    const content = rows.map((r, i) => [`${i + 1}. ${r.subject}`, r.discussions && `Discussions: ${r.discussions}`, r.action && `Action: ${r.action}`, r.ownership && `Ownership: ${r.ownership}`, r.timeline && `Timeline: ${r.timeline}`, r.remarks && `Remarks: ${r.remarks}`].filter(Boolean).join("\n")).join("\n\n");
    return { title: form.title, content, date: momDate, rows };
  };
  const printMom = (it) => {
    const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
    const hasRows = Array.isArray(it.rows) && it.rows.length > 0;
    const body = hasRows
      ? `<table><thead><tr>${MOM_COLS.map((c) => `<th>${c}</th>`).join("")}</tr></thead><tbody>${it.rows.map((r, i) => { const v = MOM_ROW(r); return `<tr><td>${i + 1}</td><td>${esc(v.subject)}</td><td>${esc(v.discussions)}</td><td>${esc(v.action)}</td><td>${esc(v.ownership)}</td><td>${esc(v.timeline)}</td><td>${esc(v.remarks)}</td></tr>`; }).join("")}</tbody></table>`
      : `<pre style="white-space:pre-wrap;font-family:inherit">${esc(it.content)}</pre>`;
    const w = window.open("", "_blank");
    if (!w) return toast.error("Popup blocked — allow popups to print");
    w.document.write(`<html><head><title>MOM ${esc(it.title || "")}</title><style>body{font-family:Arial,sans-serif;margin:24px}h2{margin:0 0 4px}p{margin:0 0 14px;color:#444}table{border-collapse:collapse;width:100%}th,td{border:1px solid #64748b;padding:6px 8px;vertical-align:top;white-space:pre-wrap;font-size:12px;text-align:left}th{background:#f1f5f9}tr{page-break-inside:avoid}@page{margin:12mm}</style></head><body><h2>Minutes of Meeting: ${esc(it.title || "")}</h2><p>Date: ${esc(it.date || "")}</p>${body}</body></html>`);
    w.document.close();
    w.focus();
    w.print();
  };
  const exportMom = async (it, fmt) => {
    if (fmt === "print") return printMom(it);
    if (exportingId) return;
    setExportingId(it.id);
    try {
      const res = await api.get(`/workspaces/${wsId}/mom/${it.id}/export`, { params: { fmt }, responseType: "blob" });
      const match = (res.headers?.["content-disposition"] || "").match(/filename="([^"]+)"/);
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = match?.[1] || `MOM.${fmt === "excel" ? "xlsx" : fmt}`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt === "excel" ? "Excel" : fmt.toUpperCase()} downloaded`);
    } catch {
      toast.error("Export failed");
    } finally {
      setExportingId(null);
    }
  };
  const create = async () => {
    if (module !== "collaborations" && !form.title.trim()) return toast.error("Title required");
    if (module === "collaborations" && !form.content.trim()) return toast.error("Message required");
    if (module === "mom") {
      const payload = momPayload();
      if (!payload) return toast.error("Add at least one MOM row");
      if (editId) {
        await api.put(`/workspaces/${wsId}/items/${module}/${editId}`, payload);
        toast.success("MOM updated");
      } else {
        await api.post(`/workspaces/${wsId}/items/${module}`, payload);
        toast.success("Added");
      }
      resetForm(); load(); onChange?.();
      return;
    }
    await api.post(`/workspaces/${wsId}/items/${module}`, { ...form });
    toast.success("Added"); setForm({ title: "", content: "" }); load(); onChange?.();
  };
  const op = async (id, o) => { await api.post(`/workspaces/${wsId}/items/${module}/${id}/op/${o}`); load(); onChange?.(); };
  const createTaskFromRow = async (it, r) => {
    const v = MOM_ROW(r);
    const title = (v.action.split("\n")[0] || v.subject || "MOM action").trim().slice(0, 200);
    try {
      const { data } = await api.post(`/workspaces/${wsId}/mom-create-task/${it.id}`, { title });
      toast.success(`Task ${data.human_id} created`);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not create task"); }
  };
  return (
    <div>
      <div className="bg-white border rounded-lg p-4 mb-4 space-y-2">
        {module !== "collaborations" && <Input data-testid="item-title" placeholder={module === "mom" ? "Meeting title" : "Note title"} value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />}
        {module === "mom" ? (
          <>
            <Input data-testid="mom-date" type="date" value={momDate} onChange={(e) => setMomDate(e.target.value)} className="max-w-48" />
            <div className="overflow-x-auto">
              <table className="w-full border-collapse text-sm">
                <thead>
                  <tr>{MOM_COLS.map((c) => <th key={c} className={`border border-slate-300 bg-slate-50 px-2 py-1.5 text-left font-semibold text-slate-700 ${c === "Sr. No." ? "w-12" : ""}`}>{c}</th>)}<th className="border border-slate-300 bg-slate-50 w-10"></th></tr>
                </thead>
                <tbody>
                  {momRows.map((r, i) => (
                    <tr key={i}>
                      <td className="border border-slate-300 px-2 py-1.5 align-top text-slate-500">{i + 1}</td>
                      <td className="border border-slate-300 p-1 align-top min-w-36"><Textarea rows={2} data-testid={`mom-row-subject-${i}`} placeholder="Subject" value={r.subject} onChange={(e) => setMomRow(i, "subject", e.target.value)} className="border-0 shadow-none focus-visible:ring-0 px-1.5" /></td>
                      <td className="border border-slate-300 p-1 align-top min-w-52"><Textarea rows={2} data-testid={`mom-row-discussions-${i}`} placeholder="Discussions (multiline / numbered points OK)" value={r.discussions} onChange={(e) => setMomRow(i, "discussions", e.target.value)} className="border-0 shadow-none focus-visible:ring-0 px-1.5" /></td>
                      <td className="border border-slate-300 p-1 align-top min-w-52"><Textarea rows={2} data-testid={`mom-row-action-${i}`} placeholder="Action / next steps (multiline / numbered points OK)" value={r.action} onChange={(e) => setMomRow(i, "action", e.target.value)} className="border-0 shadow-none focus-visible:ring-0 px-1.5" /></td>
                      <td className="border border-slate-300 p-1 align-top min-w-28"><Textarea rows={2} data-testid={`mom-row-ownership-${i}`} placeholder="Owner (person / team)" value={r.ownership} onChange={(e) => setMomRow(i, "ownership", e.target.value)} className="border-0 shadow-none focus-visible:ring-0 px-1.5" /></td>
                      <td className="border border-slate-300 p-1 align-top min-w-24"><Textarea rows={2} data-testid={`mom-row-timeline-${i}`} placeholder="Timeline / target date" value={r.timeline} onChange={(e) => setMomRow(i, "timeline", e.target.value)} className="border-0 shadow-none focus-visible:ring-0 px-1.5" /></td>
                      <td className="border border-slate-300 p-1 align-top min-w-24"><Textarea rows={2} data-testid={`mom-row-remarks-${i}`} placeholder="Remarks" value={r.remarks} onChange={(e) => setMomRow(i, "remarks", e.target.value)} className="border-0 shadow-none focus-visible:ring-0 px-1.5" /></td>
                      <td className="border border-slate-300 p-1 align-top text-center"><button data-testid={`mom-row-delete-${i}`} onClick={() => setMomRows((rs) => (rs.length > 1 ? rs.filter((_, j) => j !== i) : rs))} disabled={momRows.length === 1} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50 disabled:opacity-30"><Trash2 size={14} /></button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <Button size="sm" variant="outline" onClick={() => setMomRows((rs) => [...rs, { ...EMPTY_MOM_ROW }])} data-testid="mom-add-row"><Plus size={14} className="mr-1" /> Add Row</Button>
          </>
        ) : (
          <Textarea data-testid="item-content" placeholder={module === "collaborations" ? "Write a message…" : "Content…"} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} />
        )}
        <div className="flex items-center gap-2">
          <Button size="sm" onClick={create} data-testid="item-create">{editId ? <Pencil size={15} className="mr-1" /> : <Plus size={15} className="mr-1" />} {editId ? "Save MOM" : `Add ${module === "mom" ? "MOM" : module === "collaborations" ? "Post" : "Note"}`}</Button>
          {editId && <Button size="sm" variant="ghost" onClick={resetForm} data-testid="item-edit-cancel">Cancel</Button>}
          <button onClick={() => setShowHidden((s) => !s)} data-testid="item-show-hidden" className={`flex items-center gap-1 text-sm px-2.5 py-1.5 rounded-md border ${showHidden ? "bg-slate-800 text-white" : "text-slate-600"}`}><Eye size={14} /> {showHidden ? "Showing Hidden" : "Show Hidden"}</button>
        </div>
      </div>
      <div className="space-y-2">
        {items.length === 0 && <div className="text-sm text-slate-400">No {module} yet.</div>}
        {items.map((it) => (
          <div key={it.id} data-testid={`item-${it.id}`} className="bg-white border rounded-lg p-3">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                {it.title && <div className="font-medium text-slate-800 flex items-center gap-1.5">{it.is_hidden && <span className="text-[10px] bg-slate-100 rounded px-1"><EyeOff size={10} className="inline" /> Hidden</span>}{it.title}</div>}
                {module === "mom" && it.date && <div className="text-xs text-slate-500 mt-0.5">Date: {it.date}</div>}
                {module === "mom" && Array.isArray(it.rows) && it.rows.length > 0
                  ? <MomTable rows={it.rows} onRowTask={(r, i) => createTaskFromRow(it, r)} />
                  : <div className="text-sm text-slate-600 whitespace-pre-wrap">{it.content}</div>}
                <div className="text-[11px] text-slate-400 mt-1">{it.created_by_name} · {new Date(it.created_at).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                {module === "mom" && (
                  <div className="relative group">
                    <button data-testid={`mom-export-${it.id}`} disabled={exportingId === it.id} title="Export MOM" className="p-1.5 rounded text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 disabled:opacity-40"><Download size={14} /></button>
                    <div className="absolute right-0 mt-1 w-32 bg-white border rounded-lg shadow-lg z-20 hidden group-hover:block" data-testid={`mom-export-menu-${it.id}`}>
                      {[["excel", "Excel (.xlsx)"], ["pdf", "PDF"], ["print", "Print"], ["csv", "CSV"]].map(([f, lbl]) => (
                        <button key={f} onClick={() => exportMom(it, f)} data-testid={`mom-export-${it.id}-${f}`} className="block w-full text-left px-3 py-1.5 text-xs hover:bg-slate-50">{lbl}</button>
                      ))}
                    </div>
                  </div>
                )}
                {module === "mom" && Array.isArray(it.rows) && it.rows.length > 0 && (
                  <button data-testid={`item-edit-${it.id}`} onClick={() => startEdit(it)} title="Edit MOM" className="p-1.5 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50"><Pencil size={14} /></button>
                )}
                {module === "mom" && (
                  <button data-testid={`item-share-${it.id}`} onClick={() => setShareMom(it)} title="Share MOM" className="p-1.5 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50"><Share2 size={14} /></button>
                )}
                <button data-testid={`item-hide-${it.id}`} onClick={() => op(it.id, it.is_hidden ? "unhide" : "hide")} className="p-1.5 rounded text-slate-400 hover:bg-slate-100">{it.is_hidden ? <Eye size={14} /> : <EyeOff size={14} />}</button>
                <button data-testid={`item-delete-${it.id}`} onClick={() => op(it.id, "delete")} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={14} /></button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {shareMom && ws && <MembersModal ws={ws} onClose={() => setShareMom(null)} onDone={load} />}
    </div>
  );
}

function renderContent(text) {
  const parts = String(text || "").split(/(@\w+)/g);
  return parts.map((p, i) => p.startsWith("@")
    ? <span key={i} data-testid="mention-chip" className="text-blue-700 bg-blue-100 rounded px-1 py-0.5 font-medium">{p}</span>
    : <React.Fragment key={i}>{p}</React.Fragment>);
}

function Composer({ users, onSubmit, placeholder, testid, autoFocus, initial, submitLabel = "Post", onCancel }) {
  const [text, setText] = useState(initial?.text || "");
  const [mentions, setMentions] = useState(initial?.mentions || []);
  const [sug, setSug] = useState(null);
  const onChange = (e) => {
    const v = e.target.value;
    setText(v);
    const m = v.slice(0, e.target.selectionStart).match(/@(\w*)$/);
    if (m) { const qq = m[1].toLowerCase(); setSug(users.filter((u) => u.full_name.toLowerCase().includes(qq)).slice(0, 6)); }
    else setSug(null);
  };
  const pick = (u) => {
    setText((t) => t.replace(/@(\w*)$/, `@${u.full_name.replace(/\s+/g, "")} `));
    setMentions((m) => Array.from(new Set([...m, u.id])));
    setSug(null);
  };
  const submit = () => { if (!text.trim()) return; onSubmit(text.trim(), mentions); if (!initial) { setText(""); setMentions([]); } setSug(null); };
  return (
    <div className="relative">
      <Textarea data-testid={testid} autoFocus={autoFocus} placeholder={placeholder} value={text} onChange={onChange} rows={2} />
      {sug && sug.length > 0 && (
        <div className="absolute z-30 mt-1 w-56 bg-white border rounded-lg shadow-lg" data-testid="collab-mention-list">
          {sug.map((u) => (
            <button key={u.id} data-testid={`collab-mention-${u.id}`} onClick={() => pick(u)} className="block w-full text-left px-3 py-1.5 text-sm hover:bg-slate-50">{u.full_name}</button>
          ))}
        </div>
      )}
      <div className="mt-2 flex gap-2">
        <Button size="sm" onClick={submit} data-testid={`${testid}-submit`}><Plus size={14} className="mr-1" /> {submitLabel}</Button>
        {onCancel && <Button size="sm" variant="ghost" onClick={onCancel} data-testid={`${testid}-cancel`}>Cancel</Button>}
      </div>
    </div>
  );
}

function CollabPost({ it, isReply, onReply, showReply, op, canEdit, onEdit, highlight, onReact, myId }) {
  const ref = React.useRef(null);
  React.useEffect(() => { if (highlight && ref.current) ref.current.scrollIntoView({ behavior: "smooth", block: "center" }); }, [highlight]);
  const rx = it.reactions || {};
  const Reaction = ({ emoji }) => {
    const list = rx[emoji] || [];
    const mine = list.includes(myId);
    return (
      <button data-testid={`collab-react-${emoji === "👍" ? "up" : "heart"}-${it.id}`} onClick={() => onReact(it.id, emoji)}
        className={`text-xs px-1.5 py-0.5 rounded-full border ${mine ? "bg-blue-50 border-blue-300" : "bg-white border-slate-200 hover:bg-slate-50"}`}>
        {emoji} {list.length > 0 ? list.length : ""}
      </button>
    );
  };
  return (
    <div ref={ref} data-testid={`collab-item-${it.id}`} className={`bg-white border rounded-lg p-3 ${isReply ? "ml-8 mt-2" : ""} ${highlight ? "ring-2 ring-blue-400" : ""}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="text-sm text-slate-800 whitespace-pre-wrap">{it.is_hidden && <span className="text-[10px] bg-slate-100 rounded px-1 mr-1"><EyeOff size={10} className="inline" /> Hidden</span>}{renderContent(it.content)}{it.updated_at && it.updated_at !== it.created_at && <span className="text-[10px] text-slate-400 ml-1">(edited)</span>}</div>
          <div className="text-[11px] text-slate-400 mt-1">{it.created_by_name} · {new Date(it.created_at).toLocaleString()}</div>
          <div className="flex items-center gap-1 mt-2"><Reaction emoji="👍" /><Reaction emoji="❤️" /></div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {!isReply && <button data-testid={`collab-reply-btn-${it.id}`} onClick={() => onReply(showReply ? null : it.id)} className="text-xs text-blue-600 hover:underline px-1">Reply</button>}
          {canEdit && <button data-testid={`collab-edit-${it.id}`} onClick={() => onEdit(it.id)} className="p-1 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50"><Pencil size={13} /></button>}
          <button data-testid={`collab-hide-${it.id}`} onClick={() => op(it.id, it.is_hidden ? "unhide" : "hide")} className="p-1 rounded text-slate-400 hover:bg-slate-100">{it.is_hidden ? <Eye size={13} /> : <EyeOff size={13} />}</button>
          <button data-testid={`collab-delete-${it.id}`} onClick={() => op(it.id, "delete")} className="p-1 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"><Trash2 size={13} /></button>
        </div>
      </div>
    </div>
  );
}

function CollabPanel({ wsId, onChange, highlightId }) {
  const { users } = useData();
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [replyTo, setReplyTo] = useState(null);
  const [editId, setEditId] = useState(null);
  const load = useCallback(() => api.get(`/workspaces/${wsId}/items/collaborations`).then((r) => setItems(r.data)), [wsId]);
  useEffect(() => { load(); }, [load]);
  const post = async (content, mention_ids, parent_id) => {
    await api.post(`/workspaces/${wsId}/items/collaborations`, { content, mention_ids, parent_id: parent_id || null });
    toast.success("Posted"); setReplyTo(null); load(); onChange?.();
  };
  const saveEdit = async (id, content, mention_ids) => {
    await api.put(`/workspaces/${wsId}/items/collaborations/${id}`, { content, mention_ids });
    toast.success("Updated"); setEditId(null); load(); onChange?.();
  };
  const op = async (id, o) => { await api.post(`/workspaces/${wsId}/items/collaborations/${id}/op/${o}`); load(); onChange?.(); };
  const react = async (id, emoji) => { await api.post(`/workspaces/${wsId}/collab-react/${id}`, { emoji }); load(); };
  const roots = items.filter((i) => !i.parent_id);
  const repliesOf = (id) => items.filter((i) => i.parent_id === id);
  const canEditItem = (it) => it.created_by === user?.id || ["master_admin", "admin"].includes(user?.role);
  const renderOne = (it, isReply) => (
    editId === it.id ? (
      <div key={it.id} className={isReply ? "ml-8 mt-2" : ""}>
        <Composer users={users} autoFocus testid={`collab-edit-composer-${it.id}`} submitLabel="Save" initial={{ text: it.content, mentions: it.mention_ids || [] }}
          onSubmit={(c, m) => saveEdit(it.id, c, m)} onCancel={() => setEditId(null)} />
      </div>
    ) : (
      <CollabPost key={it.id} it={it} isReply={isReply} onReply={setReplyTo} showReply={replyTo === it.id} op={op}
        canEdit={canEditItem(it)} onEdit={setEditId} highlight={highlightId === it.id} onReact={react} myId={user?.id} />
    )
  );
  return (
    <div>
      <div className="bg-white border rounded-lg p-4 mb-4">
        <Composer users={users} testid="collab-composer" placeholder="Start a discussion… type @ to mention someone" onSubmit={(c, m) => post(c, m, null)} />
      </div>
      <div className="space-y-3">
        {roots.length === 0 && <div className="text-sm text-slate-400">No discussions yet.</div>}
        {roots.map((it) => (
          <div key={it.id}>
            {renderOne(it, false)}
            {repliesOf(it.id).map((r) => renderOne(r, true))}
            {replyTo === it.id && (
              <div className="ml-8 mt-2"><Composer users={users} autoFocus testid={`collab-reply-composer-${it.id}`} submitLabel="Reply" placeholder="Write a reply… use @ to mention" onSubmit={(c, m) => post(c, m, it.id)} onCancel={() => setReplyTo(null)} /></div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function ActivityPanel({ wsId }) {
  const [logs, setLogs] = useState([]);
  const [module, setModule] = useState("");
  const [action, setAction] = useState("");
  useEffect(() => {
    const params = new URLSearchParams();
    if (module) params.set("module", module);
    if (action) params.set("action", action);
    api.get(`/workspaces/${wsId}/activity?${params.toString()}`).then((r) => setLogs(r.data)).catch(() => {});
  }, [wsId, module, action]);
  const selCls = "text-sm border border-slate-200 rounded-lg px-2.5 py-1.5 bg-white text-slate-700 focus:outline-none focus:ring-2 focus:ring-blue-500";
  return (
    <div>
      <div className="flex items-center gap-2 mb-3" data-testid="activity-filters">
        <select data-testid="activity-filter-module" value={module} onChange={(e) => setModule(e.target.value)} className={selCls}>
          <option value="">All modules</option>
          <option value="tasks">Tasks</option>
          <option value="notes">Notes</option>
          <option value="mom">MOM</option>
          <option value="documents">Documents</option>
          <option value="collaborations">Collaborations</option>
          <option value="workspace">Workspace</option>
        </select>
        <select data-testid="activity-filter-action" value={action} onChange={(e) => setAction(e.target.value)} className={selCls}>
          <option value="">All actions</option>
          <option value="create">Created</option>
          <option value="edit">Edited</option>
          <option value="rename">Renamed</option>
          <option value="hidden">Hidden</option>
          <option value="unhidden">Unhidden</option>
          <option value="delete">Deleted</option>
          <option value="restore">Restored</option>
          <option value="member_change">Member changes</option>
          <option value="upload">Uploads</option>
        </select>
        {(module || action) && (
          <button data-testid="activity-filter-reset" onClick={() => { setModule(""); setAction(""); }} className="text-sm text-blue-600 hover:text-blue-700 font-medium">Reset</button>
        )}
      </div>
      <div className="border rounded-lg bg-white divide-y" data-testid="activity-feed">
      {logs.length === 0 && <div className="p-4 text-sm text-slate-400">No activity yet.</div>}
      {logs.map((l) => (
        <div key={l.id} data-testid={`activity-${l.id}`} className="flex items-start gap-3 p-3">
          <div className="h-7 w-7 rounded-full bg-slate-100 flex items-center justify-center shrink-0"><Activity size={13} className="text-slate-500" /></div>
          <div className="min-w-0 flex-1">
            <div className="text-sm text-slate-800"><span className="font-medium">{l.user_name}</span> · <span className="text-slate-500">{String(l.action).replace(/_/g, " ")}</span> <span className="text-xs text-slate-400">({l.entity})</span></div>
            <div className="text-[11px] text-slate-400">{new Date(l.timestamp).toLocaleString()}</div>
          </div>
        </div>
      ))}
      </div>
    </div>
  );
}

function DocumentsPanel({ wsId, onChange }) {
  const { favourites, toggleFavourite } = useData();
  const [docs, setDocs] = useState([]);
  const [showHidden, setShowHidden] = useState(false);
  const [viewDoc, setViewDoc] = useState(null);
  const [shareDoc, setShareDoc] = useState(null);
  const [remarkDoc, setRemarkDoc] = useState(null);
  const load = useCallback(() => api.get(`/workspaces/${wsId}/documents`, { params: { show_hidden: showHidden } }).then((r) => setDocs(r.data)), [wsId, showHidden]);
  useEffect(() => { load(); }, [load]);
  const upload = async (e, replaceId) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    const url = `/workspaces/${wsId}/documents${replaceId ? `?replace_id=${replaceId}` : ""}`;
    try { await api.post(url, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(replaceId ? "New version uploaded" : "Uploaded"); load(); onChange?.(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    e.target.value = "";
  };
  const op = async (id, o) => { await api.post(`/workspaces/${wsId}/documents/${id}/${o}`); load(); onChange?.(); };
  const move = async (idx, dir) => {
    const j = idx + dir;
    if (j < 0 || j >= docs.length) return;
    const next = [...docs];
    [next[idx], next[j]] = [next[j], next[idx]];
    setDocs(next);
    try { await api.post(`/workspaces/${wsId}/documents/reorder`, { ordered_ids: next.map((d) => d.id) }); }
    catch { toast.error("Reorder failed"); load(); }
  };
  const download = async (d) => {
    const res = await api.get(`/workspaces/${wsId}/documents/${d.id}/download`, { responseType: "blob" });
    const url = URL.createObjectURL(res.data); const a = document.createElement("a"); a.href = url; a.download = d.file_name; a.click();
  };
  const iconBtn = "p-1.5 rounded text-slate-400";
  return (
    <div>
      <div className="flex items-center gap-3 mb-4">
        <label className="inline-flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg px-3 py-2 cursor-pointer" data-testid="doc-upload-label">
          <Upload size={15} /> Upload Document
          <input type="file" className="hidden" data-testid="doc-upload-input" onChange={(e) => upload(e)} />
        </label>
        <button data-testid="doc-show-hidden" onClick={() => setShowHidden((s) => !s)} className={`inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border text-sm font-medium transition-colors ${showHidden ? "bg-slate-800 border-slate-800 text-white" : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          <Eye size={14} /> {showHidden ? "Showing Hidden" : "Show Hidden"}
        </button>
      </div>
      <div className="border rounded-lg bg-white divide-y">
        {docs.length === 0 && <div className="text-sm text-slate-400 p-4">No documents yet.</div>}
        {docs.map((d, idx) => (
          <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center gap-3 p-3">
            <div className="flex flex-col shrink-0">
              <button data-testid={`doc-moveup-${d.id}`} title="Move up" disabled={idx === 0} onClick={() => move(idx, -1)} className="text-slate-300 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed leading-none"><ChevronUp size={14} /></button>
              <button data-testid={`doc-movedown-${d.id}`} title="Move down" disabled={idx === docs.length - 1} onClick={() => move(idx, 1)} className="text-slate-300 hover:text-slate-600 disabled:opacity-30 disabled:cursor-not-allowed leading-none"><ChevronDown size={14} /></button>
            </div>
            <FolderArchive size={18} className="text-violet-500 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-medium text-slate-800 truncate flex items-center gap-1.5">
                {d.is_hidden && <span className="text-[10px] bg-slate-100 rounded px-1"><EyeOff size={10} className="inline" /> Hidden</span>}
                {d.display_name} <span className="text-xs text-slate-400">v{d.version}</span>
                {d.share_count > 0 && <span title={`${d.share_count} active share(s)`} className="inline-flex items-center gap-0.5 text-[10px] font-semibold text-blue-600 bg-blue-50 rounded px-1.5 py-0.5" data-testid={`doc-share-badge-${d.id}`}><Share2 size={10} /> {d.share_count}</span>}
              </div>
              <div className="text-[11px] text-slate-400">{d.file_type?.toUpperCase()} · {Math.round((d.file_size || 0) / 1024)} KB · {d.uploaded_by_name}{d.created_at ? ` · ${new Date(d.created_at).toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" })}` : ""}</div>
              {d.latest_remark && <div className="text-[11px] text-slate-500 italic truncate mt-0.5" title={d.latest_remark.remark}><MessageSquare size={10} className="inline mr-1" />{d.latest_remark.remark}</div>}
            </div>
            <button data-testid={`doc-fav-${d.id}`} onClick={() => toggleFavourite("document", d.id)} title="Favourite" className={`${iconBtn} ${favourites.document?.has?.(d.id) ? "text-amber-500" : "hover:text-amber-400 hover:bg-amber-50"}`}><Star size={15} className={favourites.document?.has?.(d.id) ? "fill-amber-500" : ""} /></button>
            <button data-testid={`doc-quickview-${d.id}`} onClick={() => setViewDoc(d)} title="Quick View" className={`${iconBtn} hover:text-violet-600 hover:bg-violet-50`}><ScanEye size={15} /></button>
            <button data-testid={`doc-share-${d.id}`} onClick={() => setShareDoc(d)} title="Share" className={`${iconBtn} hover:text-blue-600 hover:bg-blue-50`}><Share2 size={15} /></button>
            <button data-testid={`doc-remarks-${d.id}`} onClick={() => setRemarkDoc(d)} title="Remarks" className={`${iconBtn} hover:text-amber-600 hover:bg-amber-50`}><MessageSquare size={15} /></button>
            <button data-testid={`doc-download-${d.id}`} onClick={() => download(d)} title="Download" className={`${iconBtn} hover:text-blue-600 hover:bg-blue-50`}><Download size={15} /></button>
            <label title="Replace version" className={`${iconBtn} hover:text-emerald-600 hover:bg-emerald-50 cursor-pointer`}><Pencil size={15} /><input type="file" className="hidden" data-testid={`doc-replace-${d.id}`} onChange={(e) => upload(e, d.id)} /></label>
            <button data-testid={`doc-hide-${d.id}`} onClick={() => op(d.id, d.is_hidden ? "unhide" : "hide")} className={`${iconBtn} hover:bg-slate-100`}>{d.is_hidden ? <Eye size={15} /> : <EyeOff size={15} />}</button>
            <button data-testid={`doc-delete-${d.id}`} onClick={() => op(d.id, "delete")} className={`${iconBtn} hover:text-red-600 hover:bg-red-50`}><Trash2 size={15} /></button>
          </div>
        ))}
      </div>
      {viewDoc && <QuickViewModal wsId={wsId} doc={viewDoc} onClose={() => setViewDoc(null)} onDownload={download} />}
      {shareDoc && <ShareModal wsId={wsId} doc={shareDoc} onClose={() => setShareDoc(null)} onChange={load} />}
      {remarkDoc && <RemarksModal wsId={wsId} doc={remarkDoc} onClose={() => setRemarkDoc(null)} onChange={load} />}
    </div>
  );
}

function ModalShell({ title, sub, onClose, children, wide }) {
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div className={`bg-white rounded-xl shadow-xl w-full ${wide ? "max-w-4xl" : "max-w-lg"} max-h-[90vh] flex flex-col`} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="min-w-0"><div className="font-semibold text-slate-800 truncate">{title}</div>{sub && <div className="text-xs text-slate-400 truncate">{sub}</div>}</div>
          <button onClick={onClose} data-testid="modal-close" className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><X size={18} /></button>
        </div>
        <div className="p-5 overflow-auto">{children}</div>
      </div>
    </div>
  );
}

function QuickViewModal({ wsId, doc, onClose, onDownload }) {
  const [state, setState] = useState({ loading: true });
  const [blobUrl, setBlobUrl] = useState(null);
  useEffect(() => {
    let revoked = false;
    (async () => {
      try {
        const r = await api.get(`/workspaces/${wsId}/documents/${doc.id}/preview`);
        const p = r.data;
        if (p.kind === "inline") {
          const b = await api.get(`/workspaces/${wsId}/documents/${doc.id}/view`, { responseType: "blob" });
          if (!revoked) { const u = URL.createObjectURL(b.data); setBlobUrl(u); setState({ loading: false, ...p }); }
        } else setState({ loading: false, ...p });
      } catch (e) { setState({ loading: false, kind: "unsupported" }); }
    })();
    return () => { revoked = true; };
  }, [wsId, doc.id]);
  useEffect(() => () => { if (blobUrl) URL.revokeObjectURL(blobUrl); }, [blobUrl]);
  const isImg = ["png", "jpg", "jpeg", "gif", "webp"].includes((doc.file_type || "").toLowerCase());
  return (
    <ModalShell wide title={doc.display_name} sub={`${doc.file_type?.toUpperCase()} · v${doc.version}`} onClose={onClose}>
      <div data-testid="quickview-body" className="min-h-[300px]">
        {state.loading && <div className="text-sm text-slate-400 p-6 text-center">Loading preview…</div>}
        {!state.loading && state.kind === "inline" && blobUrl && (isImg
          ? <img src={blobUrl} alt={doc.display_name} className="max-w-full mx-auto" />
          : <iframe src={blobUrl} title="preview" className="w-full h-[70vh] border rounded" />)}
        {!state.loading && state.kind === "text" && <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans">{state.text || "(empty document)"}</pre>}
        {!state.loading && state.kind === "table" && (
          <div className="space-y-4">
            {(state.sheets || []).map((sh, i) => (
              <div key={i}>
                {state.sheets.length > 1 && <div className="text-xs font-semibold text-slate-500 mb-1">{sh.name}</div>}
                <div className="overflow-auto border rounded"><table className="text-xs w-full"><thead className="bg-slate-50"><tr>{sh.columns.map((c, j) => <th key={j} className="px-2 py-1 text-left font-semibold text-slate-600 border-b">{c}</th>)}</tr></thead>
                  <tbody>{sh.rows.map((row, ri) => <tr key={ri} className="odd:bg-white even:bg-slate-50/50">{row.map((cell, ci) => <td key={ci} className="px-2 py-1 border-b text-slate-700">{cell}</td>)}</tr>)}</tbody></table></div>
              </div>
            ))}
          </div>
        )}
        {!state.loading && state.kind === "unsupported" && (
          <div className="text-center py-10 text-slate-500">
            <p className="mb-3">Preview unavailable for this file type.</p>
            <Button size="sm" onClick={() => onDownload(doc)} data-testid="quickview-download"><Download size={14} className="mr-1.5" /> Download</Button>
          </div>
        )}
      </div>
    </ModalShell>
  );
}

function ShareModal({ wsId, doc, onClose, onChange }) {
  const [users, setUsers] = useState([]);
  const [shares, setShares] = useState([]);
  const [form, setForm] = useState({ shared_with_user_id: "", permission: "view", expires_at: "", create_link: false });
  const loadShares = useCallback(() => api.get(`/workspaces/${wsId}/documents/${doc.id}/shares`).then((r) => setShares(r.data)).catch(() => {}), [wsId, doc.id]);
  useEffect(() => { api.get("/users").then((r) => setUsers(r.data)).catch(() => setUsers([])); loadShares(); }, [loadShares]);
  const submit = async () => {
    if (!form.shared_with_user_id) { toast.error("Select a user"); return; }
    try {
      await api.post(`/workspaces/${wsId}/documents/${doc.id}/shares`, { ...form, expires_at: form.expires_at || null });
      toast.success("Document shared"); setForm({ shared_with_user_id: "", permission: "view", expires_at: "", create_link: false }); loadShares(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Share failed"); }
  };
  const changePerm = async (s, permission) => { await api.put(`/workspaces/${wsId}/documents/${doc.id}/shares/${s.id}`, { shared_with_user_id: s.shared_with_user_id, permission, expires_at: s.expires_at || null }); toast.success("Permission updated"); loadShares(); };
  const revoke = async (s) => { await api.post(`/workspaces/${wsId}/documents/${doc.id}/shares/${s.id}/revoke`); toast.success("Access revoked"); loadShares(); onChange?.(); };
  const copyLink = (s) => { const link = `${process.env.REACT_APP_BACKEND_URL}/api/shared-documents/${s.share_token}${s.permission === "download" ? "/download" : ""}`; navigator.clipboard?.writeText(link); toast.success("Link copied"); };
  return (
    <ModalShell title={`Share: ${doc.display_name}`} sub="Sharing does not change ownership" onClose={onClose}>
      <div className="space-y-3" data-testid="share-body">
        <select data-testid="share-user-select" className="w-full border rounded-lg h-10 px-2 text-sm" value={form.shared_with_user_id} onChange={(e) => setForm({ ...form, shared_with_user_id: e.target.value })}>
          <option value="">Select user…</option>
          {users.map((u) => <option key={u.id} value={u.id}>{u.full_name} ({u.email})</option>)}
        </select>
        <div className="flex gap-2">
          <select data-testid="share-permission" className="border rounded-lg h-10 px-2 text-sm flex-1" value={form.permission} onChange={(e) => setForm({ ...form, permission: e.target.value })}>
            <option value="view">View Only</option>
            <option value="download">View + Download</option>
          </select>
          <Input type="datetime-local" data-testid="share-expiry" className="flex-1 h-10" value={form.expires_at} onChange={(e) => setForm({ ...form, expires_at: e.target.value })} />
        </div>
        <label className="flex items-center gap-2 text-sm text-slate-600"><input type="checkbox" data-testid="share-link-toggle" checked={form.create_link} onChange={(e) => setForm({ ...form, create_link: e.target.checked })} /> Generate shareable link</label>
        <Button className="w-full" onClick={submit} data-testid="share-submit"><Share2 size={14} className="mr-1.5" /> Share</Button>
        <div className="border-t pt-3">
          <div className="text-xs font-semibold text-slate-500 mb-2">Current Shares ({shares.length})</div>
          {shares.length === 0 && <div className="text-sm text-slate-400">Not shared yet.</div>}
          <div className="space-y-2">
            {shares.map((s) => (
              <div key={s.id} data-testid={`share-row-${s.id}`} className="flex items-center gap-2 text-sm border rounded-lg p-2">
                <div className="min-w-0 flex-1">
                  <div className="truncate text-slate-700">{s.shared_with_name}</div>
                  <div className="text-[11px] text-slate-400">{s.shared_with_email} · <span className={s.status === "active" ? "text-emerald-600" : "text-red-500"}>{s.status}</span>{s.expires_at && ` · expires ${new Date(s.expires_at).toLocaleString()}`}</div>
                </div>
                {s.status === "active" && <>
                  <select className="border rounded h-8 text-xs px-1" value={s.permission} onChange={(e) => changePerm(s, e.target.value)} data-testid={`share-perm-${s.id}`}><option value="view">View</option><option value="download">Download</option></select>
                  {s.share_token && <button title="Copy link" onClick={() => copyLink(s)} data-testid={`share-copy-${s.id}`} className="p-1.5 rounded text-slate-400 hover:text-blue-600 hover:bg-blue-50"><Copy size={14} /></button>}
                  <button title="Revoke" onClick={() => revoke(s)} data-testid={`share-revoke-${s.id}`} className="p-1.5 rounded text-slate-400 hover:text-red-600 hover:bg-red-50"><X size={14} /></button>
                </>}
              </div>
            ))}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}

function RemarksModal({ wsId, doc, onClose, onChange }) {
  const { user } = useAuth();
  const [remarks, setRemarks] = useState([]);
  const [text, setText] = useState("");
  const [editing, setEditing] = useState(null);
  const load = useCallback(() => api.get(`/workspaces/${wsId}/documents/${doc.id}/remarks`).then((r) => setRemarks(r.data)).catch(() => {}), [wsId, doc.id]);
  useEffect(() => { load(); }, [load]);
  const add = async () => {
    const t = text.trim(); if (!t) return;
    try {
      if (editing) { await api.put(`/workspaces/${wsId}/documents/${doc.id}/remarks/${editing}`, { remark: t }); setEditing(null); }
      else await api.post(`/workspaces/${wsId}/documents/${doc.id}/remarks`, { remark: t });
      setText(""); load(); onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  const del = async (r) => { await api.delete(`/workspaces/${wsId}/documents/${doc.id}/remarks/${r.id}`); load(); onChange?.(); };
  return (
    <ModalShell title={`Remarks: ${doc.display_name}`} sub="Timestamped history" onClose={onClose}>
      <div data-testid="remarks-body">
        <div className="space-y-2 mb-3">
          <Textarea data-testid="remark-input" rows={2} maxLength={250} value={text} onChange={(e) => setText(e.target.value)} placeholder="Add a short remark (max 250 chars)…" />
          <div className="flex items-center justify-between">
            <span className="text-[11px] text-slate-400">{text.length}/250</span>
            <div className="flex gap-2">
              {editing && <Button size="sm" variant="ghost" onClick={() => { setEditing(null); setText(""); }}>Cancel</Button>}
              <Button size="sm" onClick={add} data-testid="remark-submit">{editing ? "Update" : "Add Remark"}</Button>
            </div>
          </div>
        </div>
        <div className="border-t pt-3 space-y-2 max-h-72 overflow-auto">
          {remarks.length === 0 && <div className="text-sm text-slate-400">No remarks yet.</div>}
          {remarks.map((r) => (
            <div key={r.id} data-testid={`remark-${r.id}`} className="text-sm border rounded-lg p-2">
              <div className="text-slate-700">{r.remark}</div>
              <div className="flex items-center justify-between mt-1">
                <div className="text-[11px] text-slate-400"><Clock size={10} className="inline mr-1" />{r.created_by_name} · {new Date(r.created_at).toLocaleString()}{r.updated_at !== r.created_at && " · edited"}</div>
                {r.created_by === user?.id && <div className="flex gap-1">
                  <button onClick={() => { setEditing(r.id); setText(r.remark); }} data-testid={`remark-edit-${r.id}`} className="p-1 rounded text-slate-400 hover:text-blue-600"><Pencil size={13} /></button>
                  <button onClick={() => del(r)} data-testid={`remark-delete-${r.id}`} className="p-1 rounded text-slate-400 hover:text-red-600"><Trash2 size={13} /></button>
                </div>}
              </div>
            </div>
          ))}
        </div>
      </div>
    </ModalShell>
  );
}

